"use client";

import { api } from "~/utils/api";
import { Button } from "../../themed/Button";
import { Input } from "../../themed/Input";
import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { cn } from "~/utils/fontsConfig";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../themed/Dialog";
import { useFollowingStore } from "~/state/store";
import { cva } from "class-variance-authority";

const FollowCommunityButton = ({
    communityId,
    hideIfFollowing,
    variant,
    className,
}: {
    communityId: string;
    hideIfFollowing?: boolean;
    variant?: "default" | "tertiary";
    className?: string;
}) => {
    const { data: session } = useSession();
    const [email, setEmail] = useState<string>("");
    const followersCtx = api.useUtils().followers;
    const followingData = api.followers.isUserFollowing.useQuery(
        { communityId, email: email || undefined },
        {
            enabled: !!session?.user.id || !!email,
        },
    );
    const [isEmailInputVisible, setIsEmailInputVisible] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const follow = api.followers.followCommunity.useMutation(undefined);
    const unfollow = api.followers.unfollowCommunity.useMutation();

    const handleFollowClick = useCallback(() => {
        if (!session?.user && !email) {
            setIsEmailInputVisible(true);
            return;
        }
        toast.loading("Sending follow request", { toastId: "follow-loading" });
        follow.mutateAsync(
            {
                communityId,
                email: email || undefined,
            },
            {
                onSuccess: () => {
                    toast.success("Followed!", {
                        autoClose: 1000,
                    });
                    setIsEmailInputVisible(false);
                    followersCtx.invalidate();
                },
                onError: () => {
                    toast.error("You're already following this community");
                },
                onSettled: () => {
                    toast.dismiss("follow-loading");
                },
            },
        );
    }, [session, email]);

    useEffect(() => {
        const loading =
            followingData.isLoading || follow.isLoading || unfollow.isLoading;
        if (!session) {
            if (isEmailInputVisible && !email) setIsDisabled(true);
            else setIsDisabled(false);
            return;
        }
        if (loading) setIsDisabled(true);
        setIsDisabled(false);
    }, [followingData, follow, unfollow, session, isEmailInputVisible, email]);

    if (followingData.data) {
        if (hideIfFollowing) return false;
        return (
            <Button
                disabled={isDisabled}
                onClick={async (e) => {
                    e.preventDefault();
                    unfollow.mutateAsync(
                        {
                            communityId,
                        },
                        {
                            onSuccess: () => {
                                followersCtx.invalidate();
                            },
                        },
                    );
                }}
                className={cn(className, "text-v3-N-400")}
                variant="tertiary"
            >
                Following
            </Button>
        );
    }

    return (
        <>
            <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="text"
                placeholder="Enter your email address"
                className={cn(!isEmailInputVisible && "hidden")}
            />
            <Button
                disabled={isDisabled}
                onClick={(e) => {
                    e.preventDefault();
                    handleFollowClick();
                }}
                variant={variant ?? "default"}
                className={cn("grow", className)}
            >
                Follow
            </Button>
        </>
    );
};

export default FollowCommunityButton;

const ConfirmUnfollowModal = ({
    open,
    setOpen,
    onUnfollow,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onUnfollow: () => void;
}) => {
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Unfollow Community</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to unfollow this community? You'll
                        no longer get any notifications for this community
                    </DialogDescription>
                </DialogHeader>
                <div className="flex w-full items-center justify-center">
                    <Button
                        onClick={() => {
                            onUnfollow();
                        }}
                        variant="secondary"
                        className="w-1/2 border border-black"
                    >
                        Unfollow
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const inputStyleClassNames = {
    shared: ``,
    default: ``,
    "light-banner": `
        rounded-full
        grow
    `,
};

const inputInnerStyleClassNames = {
    shared: ``,
    default: ``,
    "light-banner": `px-4`,
};

const inputVariants = cva(inputStyleClassNames.shared, {
    variants: {
        variant: {
            default: inputStyleClassNames.default,
            "light-banner": inputStyleClassNames["light-banner"],
        },
    },
    defaultVariants: { variant: "default" },
});

const inputInnerVariants = cva(inputInnerStyleClassNames.shared, {
    variants: {
        variant: {
            default: inputInnerStyleClassNames.default,
            "light-banner": inputInnerStyleClassNames["light-banner"],
        },
    },
    defaultVariants: { variant: "default" },
});

export const FollowCommunityButtonBySlug = ({
    communitySlug,
    variant,
    className,
    showEmailInput,
    handleShowInput,
    labels = {
        follow: "Follow",
        unfollow: "Following",
    },
}: {
    communitySlug: string;
    variant?: "light-banner" | "default";
    className?: string;
    showEmailInput?: boolean;
    handleShowInput?: () => void;
    labels?: {
        follow: string;
        unfollow: string;
    };
}) => {
    const session = useSession();
    const [inputEmail, setInputEmail] = useState<string>("");
    const { email: storeEmail, setEmail: setStoreEmail } = useFollowingStore();
    const [isButtonFocused, setIsButtonFocused] = useState(false);

    const [email, setEmail] = useState<string>("");

    const followersCtx = api.useUtils().followers;
    const { data: communityId } = api.community.getCommunityIdBySlug.useQuery({
        slug: communitySlug,
    });
    const followingData = api.followers.isUserFollowing.useQuery(
        {
            communityId: communityId ?? "",
            email: email || storeEmail || undefined,
        },
        {
            enabled: !!communityId,
        },
    );
    const [isEmailInputVisible, setIsEmailInputVisible] = useState(
        showEmailInput ?? false,
    );
    useEffect(() => {
        setIsEmailInputVisible(showEmailInput ?? false);
    }, [showEmailInput]);
    const [isDisabled, setIsDisabled] = useState(false);
    const follow = api.followers.followCommunity.useMutation(undefined);
    const unfollow = api.followers.unfollowCommunity.useMutation();
    const [isConfirmUnfollowOpen, setIsConfirmUnfollowOpen] = useState(false);

    const handleFollowClick = useCallback(() => {
        if (!session.data?.user && !inputEmail) {
            setIsEmailInputVisible(true);
            return;
        }

        if (!communityId) return;

        toast.loading("Sending follow request", { toastId: "follow-loading" });
        follow.mutateAsync(
            {
                communityId,
                email: inputEmail || undefined,
            },
            {
                onSuccess: () => {
                    toast.success("Followed!", {
                        autoClose: 1000,
                    });
                    setIsEmailInputVisible(false);
                    setEmail(inputEmail);
                    setStoreEmail(inputEmail);
                    followersCtx.invalidate();
                },
                onError: () => {
                    toast.error("You're already following this community");
                },
                onSettled: () => {
                    toast.dismiss("follow-loading");
                },
            },
        );
    }, [session, inputEmail, communityId]);

    useEffect(() => {
        const loading =
            followingData.isLoading || follow.isLoading || unfollow.isLoading;
        if (isEmailInputVisible && handleShowInput) handleShowInput();
        if (!session) {
            if (isEmailInputVisible && !inputEmail) setIsDisabled(true);
            else if (
                isEmailInputVisible &&
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(inputEmail)
            )
                setIsDisabled(true);
            else setIsDisabled(false);
            return;
        }
        if (loading) setIsDisabled(true);
        setIsDisabled(false);
    }, [
        followingData,
        follow,
        unfollow,
        session,
        isEmailInputVisible,
        inputEmail,
    ]);

    // Listen to keyboard keys for enter, then, submit the form
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter" && isButtonFocused) {
                event.preventDefault();
                handleFollowClick();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleFollowClick]);

    if (followingData.data) {
        return (
            <>
                <Button
                    disabled={isDisabled}
                    onClick={async (e) => {
                        e.preventDefault();

                        if (!communityId) return;

                        setIsConfirmUnfollowOpen(true);
                    }}
                    className="w-[90px] bg-white/10 backdrop-blur-md md:bg-transparent"
                    variant="tertiary"
                >
                    {labels.unfollow}
                </Button>
                <ConfirmUnfollowModal
                    open={isConfirmUnfollowOpen}
                    setOpen={setIsConfirmUnfollowOpen}
                    onUnfollow={() => {
                        if (!communityId) return;
                        if (!session.data?.user) {
                            setStoreEmail(null);
                            setEmail("");
                            followersCtx.invalidate();
                            setIsConfirmUnfollowOpen(false);
                            return;
                        }

                        unfollow.mutateAsync(
                            {
                                communityId,
                            },
                            {
                                onSuccess: () => {
                                    followersCtx.invalidate();
                                    setIsConfirmUnfollowOpen(false);
                                },
                            },
                        );
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Input
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value.toLowerCase())}
                type="text"
                placeholder="Enter your email address"
                className={cn(
                    inputVariants({ variant }),
                    !isEmailInputVisible && "hidden",
                )}
                inputClassName={cn(inputInnerVariants({ variant }))}
                onFocus={() => setIsButtonFocused(true)}
                onBlur={() => setIsButtonFocused(false)}
            />
            <Button
                disabled={isDisabled || session.status === "loading"}
                onClick={(e) => {
                    e.preventDefault();
                    handleFollowClick();
                }}
                className="bg-white/10 backdrop-blur-md disabled:bg-opacity-75 disabled:text-white disabled:opacity-90 md:bg-transparent"
                variant="tertiary"
            >
                {labels.follow}
            </Button>
        </>
    );
};
